import {
  HeartIcon,
  UsersIcon,
  GlobeAltIcon,
  ChartBarIcon,
  StarIcon,
  TruckIcon,
  BuildingStorefrontIcon,
} from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";

export default function SuccessStories() {
  const featuredStories = [
    {
      id: 1,
      title: "Colombo Kitchen's Zero Waste Journey",
      category: "Restaurant Partner",
      location: "Colombo, Sri Lanka",
      image:
        "https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=400&h=300&fit=crop",
      excerpt:
        "How a local restaurant reduced food waste by 60% while feeding 200+ families monthly",
      stats: {
        mealsShared: "2,500+",
        wasteReduced: "60%",
        familiesHelped: "200+",
        timePeriod: "6 months",
      },
      fullStory:
        "Colombo Kitchen joined MealBridge in early 2023 with a simple goal: reduce their food waste. What started as a small initiative quickly grew into a community movement. Through strategic planning and our platform, they now donate surplus food daily, helping over 200 families while cutting their waste disposal costs by 60%.",
    },
    {
      id: 2,
      title: "The Volunteer Network That Changed Everything",
      category: "Community Impact",
      location: "Kandy, Sri Lanka",
      image:
        "https://images.unsplash.com/photo-1559027615-cd4628902d85?w=400&h=300&fit=crop",
      excerpt:
        "A group of university students created a delivery network that serves 5 districts",
      stats: {
        volunteers: "50+",
        districtsServed: "5",
        deliveriesCompleted: "1,200+",
        timePeriod: "1 year",
      },
      fullStory:
        "What began as a university project has become one of our most successful volunteer networks. These dedicated students organized a delivery system that now serves 5 districts around Kandy, completing over 1,200 deliveries and creating lasting connections between donors and recipients.",
    },
    {
      id: 3,
      title: "From Food Insecurity to Food Security",
      category: "Recipient Success",
      location: "Galle, Sri Lanka",
      image:
        "https://images.unsplash.com/photo-1544027993-37dbfe43562a?w=400&h=300&fit=crop",
      excerpt:
        "How MealBridge helped a single mother feed her family and start a small business",
      stats: {
        mealsReceived: "150+",
        monthsActive: "8",
        businessStarted: "1",
        incomeGenerated: "Rs. 25,000/month",
      },
      fullStory:
        "Priya, a single mother of three, struggled to provide nutritious meals for her family. Through MealBridge, she not only received regular food assistance but also learned about food preparation and preservation. This knowledge helped her start a small catering business, generating additional income for her family.",
    },
  ];

  const impactStats = [
    {
      label: "Total Meals Shared",
      value: "50,000+",
      icon: <HeartIcon className="w-5 h-5" />,
    },
    {
      label: "Families Helped",
      value: "2,500+",
      icon: <UsersIcon className="w-5 h-5" />,
    },
    {
      label: "Partner Businesses",
      value: "150+",
      icon: <BuildingStorefrontIcon className="w-5 h-5" />,
    },
    {
      label: "Cities Covered",
      value: "25",
      icon: <GlobeAltIcon className="w-5 h-5" />,
    },
    {
      label: "Volunteer Hours",
      value: "15,000+",
      icon: <TruckIcon className="w-5 h-5" />,
    },
    {
      label: "Waste Prevented",
      value: "25,000 kg",
      icon: <ChartBarIcon className="w-5 h-5" />,
    },
  ];

  const testimonials = [
    {
      name: "Dr. Priya Fernando",
      role: "Founder, Colombo Kitchen",
      quote:
        "MealBridge transformed our business model. We're not just reducing waste anymore - we're building a community.",
      image:
        "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=80&h=80&fit=crop&crop=face",
      rating: 5,
    },
    {
      name: "Rajesh Kumar",
      role: "Volunteer Coordinator",
      quote:
        "The impact we're making is incredible. Every delivery feels like we're changing someone's day for the better.",
      image:
        "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=80&h=80&fit=crop&crop=face",
      rating: 5,
    },
    {
      name: "Sarah Perera",
      role: "Recipient & Small Business Owner",
      quote:
        "MealBridge didn't just help me feed my family - it gave me the knowledge and confidence to start my own business.",
      image:
        "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=80&h=80&fit=crop&crop=face",
      rating: 5,
    },
  ];

  const monthlyHighlights = [
    {
      month: "December 2023",
      title: "Holiday Food Drive Success",
      description:
        "Our community came together to share 5,000+ meals during the holiday season",
      impact: "5,000+ meals shared",
    },
    {
      month: "November 2023",
      title: "New Partner Onboarding",
      description: "Welcomed 25 new restaurant partners across 5 cities",
      impact: "25 new partners",
    },
    {
      month: "October 2023",
      title: "Volunteer Recognition Program",
      description:
        "Launched our volunteer appreciation program with 100+ active volunteers",
      impact: "100+ volunteers recognized",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/20">
      {/* Hero Section */}
      <div className="py-16 text-white bg-gradient-to-br from-emerald-600 via-teal-700 to-cyan-600">
        <div className="px-4 mx-auto text-center max-w-7xl sm:px-6 lg:px-8">
          <HeartIcon className="w-16 h-16 mx-auto mb-6 text-white" />
          <h1 className="mb-6 text-3xl font-bold md:text-4xl lg:text-5xl">
            Success Stories
          </h1>
          <p className="max-w-3xl mx-auto text-lg text-white/95 font-medium md:text-xl">
            Discover how MealBridge is transforming communities across Sri Lanka
            through the power of food sharing
          </p>
        </div>
      </div>

      <div className="px-4 py-12 mx-auto max-w-7xl sm:px-6 lg:px-8">
        {/* Impact Stats */}
        <section className="mb-16">
          <div className="text-center mb-12">
            <h2 className="mb-6 text-2xl font-bold text-slate-900 lg:text-3xl">
              Our Impact in Numbers
            </h2>
            <p className="max-w-2xl mx-auto text-base text-slate-600 font-medium">
              Real numbers that show the difference we're making together
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {impactStats.map((stat, index) => (
              <div key={index} className="text-center card-dashboard">
                <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-xl shadow-lg">
                  {stat.icon}
                </div>
                <h3 className="mb-2 text-2xl font-bold text-slate-900">
                  {stat.value}
                </h3>
                <p className="text-sm font-medium text-slate-600">
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Featured Stories */}
        <section className="mb-16">
          <div className="text-center mb-12">
            <h2 className="mb-6 text-2xl font-bold text-slate-900 lg:text-3xl">
              Featured Success Stories
            </h2>
            <p className="max-w-2xl mx-auto text-base text-slate-600 font-medium">
              Real stories from real people making a real difference
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
            {featuredStories.map((story, index) => (
              <div key={index} className="card-dashboard overflow-hidden">
                <div className="relative h-48 mb-4">
                  <img
                    src={story.image}
                    alt={story.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="px-2 py-1 text-xs font-medium text-white bg-emerald-600 rounded-full">
                      {story.category}
                    </span>
                  </div>
                </div>

                <div className="p-6">
                  <div className="flex items-center mb-2 text-xs text-slate-500">
                    <GlobeAltIcon className="w-4 h-4 mr-1" />
                    {story.location}
                  </div>

                  <h3 className="mb-3 text-lg font-bold text-slate-900">
                    {story.title}
                  </h3>

                  <p className="mb-4 text-sm text-slate-600">{story.excerpt}</p>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    {Object.entries(story.stats).map(
                      ([key, value], statIndex) => (
                        <div key={statIndex} className="text-center">
                          <div className="text-lg font-bold text-emerald-600">
                            {value}
                          </div>
                          <div className="text-xs text-slate-500 capitalize">
                            {key.replace(/([A-Z])/g, " $1").trim()}
                          </div>
                        </div>
                      )
                    )}
                  </div>

                  <Link
                    to="/success-stories"
                    className="w-full px-4 py-2 text-sm font-medium text-emerald-600 border border-emerald-600 rounded-lg hover:bg-emerald-50 text-center block"
                  >
                    Read Full Story
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Testimonials */}
        <section className="mb-16">
          <div className="text-center mb-12">
            <h2 className="mb-6 text-2xl font-bold text-slate-900 lg:text-3xl">
              What People Are Saying
            </h2>
            <p className="max-w-2xl mx-auto text-base text-slate-600 font-medium">
              Hear from the people whose lives have been changed by MealBridge
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="card-dashboard">
                <div className="flex items-center mb-4 space-x-3">
                  <div className="w-12 h-12 overflow-hidden rounded-full">
                    <img
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-slate-900">
                      {testimonial.name}
                    </h4>
                    <p className="text-xs text-slate-600">{testimonial.role}</p>
                    <div className="flex items-center mt-1">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <StarIcon
                          key={i}
                          className="w-3 h-3 text-yellow-400 fill-current"
                        />
                      ))}
                    </div>
                  </div>
                </div>
                <p className="text-sm text-slate-600 italic">
                  "{testimonial.quote}"
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Monthly Highlights */}
        <section className="mb-16">
          <div className="text-center mb-12">
            <h2 className="mb-6 text-2xl font-bold text-slate-900 lg:text-3xl">
              Recent Highlights
            </h2>
            <p className="max-w-2xl mx-auto text-base text-slate-600 font-medium">
              Monthly achievements and community milestones
            </p>
          </div>

          <div className="space-y-6">
            {monthlyHighlights.map((highlight, index) => (
              <div key={index} className="card-dashboard">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      <span className="px-2 py-1 text-xs font-medium text-emerald-600 bg-emerald-100 rounded-full">
                        {highlight.month}
                      </span>
                    </div>
                    <h3 className="mb-2 text-lg font-bold text-slate-900">
                      {highlight.title}
                    </h3>
                    <p className="text-sm text-slate-600">
                      {highlight.description}
                    </p>
                  </div>
                  <div className="ml-6 text-right">
                    <div className="text-lg font-bold text-emerald-600">
                      {highlight.impact}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Call to Action */}
        <section className="text-center">
          <div className="card-dashboard bg-gradient-to-r from-emerald-50 to-teal-50 border-2 border-emerald-200">
            <h2 className="mb-4 text-xl font-bold text-slate-900 lg:text-2xl">
              Be Part of Our Success Story
            </h2>
            <p className="max-w-2xl mx-auto mb-8 text-sm text-slate-700 font-medium">
              Join thousands of people making a real difference in their
              communities. Your story could be next!
            </p>
            <div className="flex flex-col justify-center gap-4 sm:flex-row">
              <Link
                to="/auth"
                className="px-6 py-3 text-sm font-medium text-white bg-emerald-600 rounded-lg border border-emerald-600 text-center"
              >
                Start Your Journey
              </Link>
              <Link
                to="/contact"
                className="px-6 py-3 text-sm font-medium text-slate-700 bg-white rounded-lg border border-slate-300 text-center"
              >
                Share Your Story
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
